import React, {
  ElementType,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Avatar,
  Box,
  Button,
  Flex,
  FormControl,
  HStack,
  Icon,
  IconButton,
  Image,
  Spinner,
  Stack,
  Text,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { AddIcon, ArrowBackIcon, CloseIcon } from '@chakra-ui/icons';
import { FaPaperclip, FaPaperPlane } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { getUserProfile } from '../../hooks/users';
import {
  fetchConversation,
  fetchConversations,
  fetchTeamChatMessages,
  sendTeamChatMessage,
} from '../../hooks/teamchat';
import { TeamChatConversation, TeamChatMessage, User } from '../../utils/types';
import {
  NewChatButton,
  TeamChatSearchInput,
  TeamChatShell,
  TeamChatSidebar,
  TeamChatSidebarEmpty,
  TeamChatSidebarHeader,
  formatChatDayLabel,
  formatChatTime,
  getConversationLabel,
  teamChatAccent,
  teamChatThreadBg,
} from './components/teamChatUi';

type RenderableChatItem =
  | { kind: 'separator'; id: string; label: string }
  | { kind: 'message'; id: string; message: TeamChatMessage };

const isImageAttachment = (contentType?: string, filename?: string) => {
  if (contentType?.startsWith('image/')) {
    return true;
  }

  return /\.(png|jpg|jpeg|gif|webp|bmp|svg)$/i.test(filename || '');
};

const getMessagePreview = (message: TeamChatMessage) => {
  if (message.content?.trim()) {
    return message.content;
  }

  if (message.attachments?.length) {
    return message.attachments.length === 1 ? 'Attachment' : `${message.attachments.length} attachments`;
  }

  return message.message_type === 'system' ? 'System update' : 'New message';
};

export function TeamChatConversationPage() {
  const { orgId, conversationId } = useParams<{ orgId: string; conversationId: string }>();
  const [conversations, setConversations] = useState<TeamChatConversation[]>([]);
  const [conversation, setConversation] = useState<TeamChatConversation>();
  const [messages, setMessages] = useState<TeamChatMessage[]>([]);
  const [loadingConversation, setLoadingConversation] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [text, setText] = useState('');
  const [search, setSearch] = useState('');
  const [user, setUser] = useState<User>();
  const [attachments, setAttachments] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const loadConversations = async () => {
    if (!orgId) {
      return;
    }

    const response = await fetchConversations(orgId);
    if (response.success && Array.isArray(response.data)) {
      setConversations(response.data);
    }
  };

  const loadConversation = async (showError = true, showLoading = true) => {
    if (!orgId || !conversationId) {
      return;
    }

    if (showLoading) {
      setLoadingConversation(true);
    }
    try {
      const response = await fetchConversation(orgId, conversationId);
      if (response.success) {
        setConversation(response.data);
      } else if (showError) {
        toast({ title: 'Failed to load conversation', status: 'error' });
      }
    } catch {
      if (showError) {
        toast({ title: 'Failed to load conversation', status: 'error' });
      }
    } finally {
      if (showLoading) {
        setLoadingConversation(false);
      }
    }
  };

  const loadMessages = async (showError = true, showLoading = true) => {
    if (!orgId || !conversationId) {
      return;
    }

    if (showLoading) {
      setLoadingMessages(true);
    }
    try {
      const response = await fetchTeamChatMessages(orgId, conversationId);
      if (response.success) {
        const nextMessages = Array.isArray(response.data)
          ? response.data.filter((message) => message.conversation === conversationId)
          : [];
        setMessages(nextMessages);
      } else if (showError) {
        toast({ title: 'Failed to load messages', status: 'error' });
      }
    } catch {
      if (showError) {
        toast({ title: 'Failed to load messages', status: 'error' });
      }
    } finally {
      if (showLoading) {
        setLoadingMessages(false);
      }
    }
  };

  useEffect(() => {
    if (!orgId) {
      return;
    }

    loadConversations();
  }, [orgId]);

  useEffect(() => {
    if (!orgId || !conversationId) {
      return;
    }

    loadConversation();
  }, [conversationId, orgId, toast]);

  useEffect(() => {
    if (!orgId || !conversationId) {
      return;
    }

    loadMessages();
  }, [conversationId, orgId, toast]);

  useEffect(() => {
    if (!orgId || !conversationId) {
      return;
    }

    const interval = window.setInterval(() => {
      loadMessages(false, false);
      loadConversations();
    }, 5000);

    return () => window.clearInterval(interval);
  }, [conversationId, orgId]);

  useEffect(() => {
    const handleNotification = (event: Event) => {
      const customEvent = event as CustomEvent<{ data?: Record<string, string> }>;
      const notifiedConversationId = customEvent.detail?.data?.conversation_id;
      if (!notifiedConversationId) {
        return;
      }

      loadConversations();
      if (notifiedConversationId === conversationId) {
        loadMessages(false, false);
        loadConversation(false, false);
      }
    };

    window.addEventListener('pipeline:notification', handleNotification as EventListener);
    return () => {
      window.removeEventListener('pipeline:notification', handleNotification as EventListener);
    };
  }, [conversationId, orgId]);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const response = await getUserProfile();
        if (response.success) {
          setUser(response.data);
        }
      } catch {
        toast({ title: 'Failed to load your account', status: 'error' });
      }
    };

    loadUser();
  }, [toast]);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, sending]);

  const filteredConversations = useMemo(
    () =>
      conversations.filter((item) => {
        const index = `${item.name || ''} ${item.recipient_name || ''} ${item.last_message?.content || ''}`.toLowerCase();
        return index.includes(search.toLowerCase());
      }).sort((left, right) => {
        const leftTime = left.last_message?.created_at || left.updated_at || '';
        const rightTime = right.last_message?.created_at || right.updated_at || '';
        return new Date(rightTime).getTime() - new Date(leftTime).getTime();
      }),
    [conversations, search],
  );

  const renderedMessages = useMemo<RenderableChatItem[]>(() => {
    const items: RenderableChatItem[] = [];
    let lastDay = '';

    messages.forEach((message) => {
      const dayLabel = formatChatDayLabel(message.created_at);
      if (dayLabel && dayLabel !== lastDay) {
        items.push({
          kind: 'separator',
          id: `separator-${dayLabel}-${message.id}`,
          label: dayLabel,
        });
        lastDay = dayLabel;
      }

      items.push({
        kind: 'message',
        id: String(message.id),
        message,
      });
    });

    return items;
  }, [messages]);

  const selectedConversationName = conversation
    ? getConversationLabel(conversation)
    : 'Conversation';

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      return;
    }

    setAttachments(Array.from(event.target.files));
  };

  const removeAttachment = (index: number) => {
    setAttachments((current) => current.filter((_, attachmentIndex) => attachmentIndex !== index));
  };

  const handleSend = async () => {
    if (!conversation || !orgId || !conversationId) {
      return;
    }

    if (!text.trim() && attachments.length === 0) {
      return;
    }

    setSending(true);
    const formData = new FormData();
    formData.append('content', text.trim());

    const nextMessageType =
      attachments.length > 0
        ? attachments.every((file) => file.type.startsWith('image/'))
          ? 'image'
          : 'file'
        : 'text';

    formData.append('message_type', nextMessageType);
    attachments.forEach((file) => formData.append('attachments', file));

    try {
      const response = await sendTeamChatMessage(orgId, conversationId, formData);
      if (response.success) {
        const newMessage = response.data as TeamChatMessage;
        setMessages((current) => [...current, newMessage]);
        setConversations((current) =>
          current.map((item) =>
            item.id === conversationId
              ? { ...item, last_message: newMessage, updated_at: newMessage.created_at }
              : item,
          ),
        );
        setText('');
        setAttachments([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        await loadConversation(false, false);
        await loadConversations();
      } else {
        toast({ title: 'Send failed', status: 'error' });
      }
    } catch {
      toast({ title: 'Send failed', status: 'error' });
    } finally {
      setSending(false);
    }
  };

  const handleComposerKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <TeamChatShell
      sidebar={
        <TeamChatSidebar
          mobileHidden
          header={
            <TeamChatSidebarHeader
              title="Team chat"
              subtitle={`${conversations.length} conversation${conversations.length === 1 ? '' : 's'}`}
              action={<NewChatButton onClick={() => navigate(`/organization/${orgId}/teamchat/select/`)} />}
            />
          }
          search={
            <TeamChatSearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search chats"
            />
          }
          conversations={filteredConversations}
          activeConversationId={conversationId}
          onSelectConversation={(nextConversationId) =>
            navigate(`/organization/${orgId}/teamchat/conversations/${nextConversationId}`)
          }
          emptyState={
            <TeamChatSidebarEmpty
              title="No chats yet"
              description="Start a direct chat to begin collaborating with your team."
            />
          }
        />
      }
      thread={
        <Box flex={1} bg={teamChatThreadBg} display="flex" flexDirection="column" minW={0}>
          <HStack
            px={{ base: 3, md: 4 }}
            py={3}
            spacing={3}
            bg="#F0F2F5"
            borderBottomWidth="1px"
            borderColor="gray.200"
            position="sticky"
            top={0}
            zIndex={3}
          >
            <IconButton
              aria-label="Back to chats"
              icon={<ArrowBackIcon />}
              variant="ghost"
              display={{ base: 'inline-flex', md: 'none' }}
              onClick={() => navigate(`/organization/${orgId}/teamchat/conversations/`)}
            />
            <Avatar
              size="sm"
              name={selectedConversationName}
              bg={teamChatAccent}
              color="white"
            />
            <Box flex={1} minW={0}>
              <Text fontWeight="800" color="gray.900" noOfLines={1}>
                {selectedConversationName}
              </Text>
              <Text fontSize="sm" color="gray.500" noOfLines={1}>
                {conversation?.is_group
                  ? 'Group conversation'
                  : loadingConversation
                    ? 'Loading details...'
                    : 'Direct conversation'}
              </Text>
            </Box>
            <IconButton
              aria-label="Start a new chat"
              icon={<AddIcon />}
              variant="ghost"
              onClick={() => navigate(`/organization/${orgId}/teamchat/select/`)}
            />
          </HStack>

          <Box
            flex={1}
            overflowY="auto"
            px={{ base: 3, md: 5 }}
            py={4}
            bgImage="radial-gradient(rgba(17, 24, 39, 0.05) 0.8px, transparent 0.8px)"
            bgSize="18px 18px"
          >
            {loadingMessages || loadingConversation ? (
              <Flex align="center" justify="center" minH="100%">
                <Spinner color={teamChatAccent} />
              </Flex>
            ) : renderedMessages.length === 0 ? (
              <VStack justify="center" align="center" minH="100%" spacing={3}>
                <Avatar size="xl" name={selectedConversationName} bg={teamChatAccent} color="white" />
                <Text fontWeight="800" color="gray.800">
                  No messages yet
                </Text>
                <Text color="gray.500" fontSize="sm" textAlign="center" maxW="320px">
                  Start the conversation. Messages and attachments will appear here as soon as you send them.
                </Text>
              </VStack>
            ) : (
              <VStack align="stretch" spacing={3}>
                {renderedMessages.map((entry) => {
                  if (entry.kind === 'separator') {
                    return (
                      <Flex key={entry.id} justify="center" py={2}>
                        <Box
                          bg="rgba(255,255,255,0.82)"
                          borderRadius="999px"
                          px={3}
                          py={1.5}
                          boxShadow="0 4px 12px rgba(15, 23, 42, 0.04)"
                        >
                          <Text fontSize="xs" color="gray.600" fontWeight="700">
                            {entry.label}
                          </Text>
                        </Box>
                      </Flex>
                    );
                  }

                  const message = entry.message;
                  const isMine = message.sender_user?.id === user?.id;
                  const bubbleBg = message.message_type === 'system'
                    ? 'rgba(255,255,255,0.92)'
                    : isMine
                      ? '#D9FDD3'
                      : 'white';

                  return (
                    <Flex
                      key={entry.id}
                      justify={message.message_type === 'system' ? 'center' : isMine ? 'flex-end' : 'flex-start'}
                    >
                      <Box
                        maxW={{ base: '88%', md: '74%' }}
                        bg={bubbleBg}
                        borderRadius={message.message_type === 'system' ? '16px' : '18px'}
                        borderTopRightRadius={message.message_type === 'system' ? '16px' : isMine ? '6px' : '18px'}
                        borderTopLeftRadius={message.message_type === 'system' ? '16px' : isMine ? '18px' : '6px'}
                        boxShadow="0 1px 2px rgba(15, 23, 42, 0.08)"
                        px={3.5}
                        py={2.5}
                      >
                        {message.message_type === 'system' ? (
                          <Text fontSize="sm" color="gray.600" textAlign="center">
                            {message.content || getMessagePreview(message)}
                          </Text>
                        ) : (
                          <Stack spacing={2}>
                            {message.content ? (
                              <Text whiteSpace="pre-wrap" fontSize="sm" color="gray.800">
                                {message.content}
                              </Text>
                            ) : null}

                            {message.attachments?.length ? (
                              <Stack spacing={2}>
                                {message.attachments.map((attachment) => {
                                  const imageLike = isImageAttachment(
                                    attachment.content_type,
                                    attachment.filename,
                                  );

                                  return imageLike ? (
                                    <Box key={attachment.id} overflow="hidden" borderRadius="14px">
                                      <Image
                                        src={attachment.file}
                                        alt={attachment.filename || 'Attachment'}
                                        borderRadius="14px"
                                        maxH="280px"
                                        objectFit="cover"
                                      />
                                      {attachment.filename ? (
                                        <Text mt={1.5} fontSize="xs" color="gray.500">
                                          {attachment.filename}
                                        </Text>
                                      ) : null}
                                    </Box>
                                  ) : (
                                    <HStack
                                      key={attachment.id}
                                      spacing={3}
                                      bg="blackAlpha.50"
                                      px={3}
                                      py={2}
                                      borderRadius="12px"
                                      align="center"
                                    >
                                      <Icon as={FaPaperclip as ElementType} color="gray.500" />
                                      <Box minW={0}>
                                        <Text fontSize="sm" color="gray.700" noOfLines={1}>
                                          {attachment.filename || 'Attachment'}
                                        </Text>
                                        {attachment.size ? (
                                          <Text fontSize="xs" color="gray.500">
                                            {(attachment.size / 1024).toFixed(1)} KB
                                          </Text>
                                        ) : null}
                                      </Box>
                                    </HStack>
                                  );
                                })}
                              </Stack>
                            ) : null}

                            <HStack justify="flex-end" spacing={1}>
                              <Text fontSize="11px" color="gray.500">
                                {formatChatTime(message.created_at)}
                              </Text>
                            </HStack>
                          </Stack>
                        )}
                      </Box>
                    </Flex>
                  );
                })}
                <div ref={bottomRef} />
              </VStack>
            )}
          </Box>

          <Box
            bg="#F0F2F5"
            borderTopWidth="1px"
            borderColor="gray.200"
            px={{ base: 3, md: 4 }}
            py={3}
            position="sticky"
            bottom={0}
          >
            {attachments.length > 0 ? (
              <HStack spacing={2} overflowX="auto" pb={3}>
                {attachments.map((file, index) => (
                  <HStack
                    key={`${file.name}-${index}`}
                    spacing={2}
                    bg="white"
                    px={3}
                    py={2}
                    borderRadius="14px"
                    borderWidth="1px"
                    borderColor="gray.200"
                    minW="fit-content"
                  >
                    <Text fontSize="sm" color="gray.700" noOfLines={1} maxW="180px">
                      {file.name}
                    </Text>
                    <IconButton
                      aria-label="Remove attachment"
                      icon={<CloseIcon boxSize={2.5} />}
                      size="xs"
                      variant="ghost"
                      onClick={() => removeAttachment(index)}
                    />
                  </HStack>
                ))}
              </HStack>
            ) : null}

            <HStack align="flex-end" spacing={2}>
              <IconButton
                aria-label="Attach files"
                icon={<Icon as={FaPaperclip as ElementType} />}
                variant="ghost"
                color="gray.600"
                borderRadius="14px"
                onClick={() => fileInputRef.current?.click()}
              />
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <FormControl>
                <Textarea
                  placeholder="Type a message"
                  resize="none"
                  minH="48px"
                  maxH="140px"
                  rows={1}
                  borderRadius="18px"
                  bg="white"
                  value={text}
                  onChange={(event) => setText(event.target.value)}
                  onKeyDown={handleComposerKeyDown}
                  _focusVisible={{
                    borderColor: 'green.400',
                    boxShadow: '0 0 0 1px rgba(31, 122, 92, 0.35)',
                  }}
                />
              </FormControl>
              <Button
                onClick={handleSend}
                isLoading={sending}
                isDisabled={sending || (!text.trim() && attachments.length === 0)}
                bg={teamChatAccent}
                color="white"
                borderRadius="16px"
                px={5}
                _hover={{ bg: '#17664D' }}
                _active={{ bg: '#145A44' }}
                rightIcon={<Icon as={FaPaperPlane as ElementType} />}
              >
                Send
              </Button>
            </HStack>
          </Box>
        </Box>
      }
    />
  );
}
